<script setup lang="ts">
import type { Follower } from '@/types/profile';
import type { PaginationInfo } from '@/types/common';

const props = defineProps({
	isAdmin: {
		type: Boolean,
		default: false,
	},
	isLoading: {
		type: Boolean,
		default: false,
	},
	groupId: {
		type: Number as PropType<number | null>,
		default: null,
	},
});

const profileStore = useProfileStore();
const groupStore = useGroupStore();
const notificationStore = useNotificationStore();
const { isTablet } = useWindowResize();

const emit = defineEmits<{
	(e: 'onMessage', userId: number, userName: string): void;
}>();

const search = useDebouncedFn('', 1000);
const currentPage = ref(1);
const sortMethod = ref(FOLLOWERS_SORTING_METHOD.OLDEST_ADDED);
const isProcessing = ref(false);
const processingId = ref<number | null>(null);
const processingName = ref<string | null>(null);
const isConfirmPromote = ref(false);
const isConfirmDemote = ref(false);
const followerData = ref<Follower[]>([]);
const paginationInfo = ref<PaginationInfo | null>(null);

const isLoading = computed(() => props.isLoading || isProcessing.value);

const headers = computed(() => {
	if (isTablet.value) {
		return [
			{
				title: 'Name',
				align: ALIGNMENT.START,
				key: 'name',
				sortable: false,
			},
		];
	}

	if (props.isAdmin) {
		return [
			{
				title: 'Name',
				width: 'auto',
				align: ALIGNMENT.START,
				key: 'name',
				sortable: false,
			},
			{
				title: 'Role',
				width: '200px',
				align: ALIGNMENT.CENTER,
				key: 'role',
				sortable: false,
			},
		];
	}

	return [
		{
			title: 'Name',
			width: '25%',
			align: ALIGNMENT.START,
			key: 'name',
			sortable: false,
		},
		{
			title: 'Role',
			width: '25%',
			align: ALIGNMENT.CENTER,
			key: 'role',
			sortable: false,
		},
		{
			title: 'Add / Remove Co-organizer',
			width: '25%',
			align: ALIGNMENT.CENTER,
			key: 'action',
			sortable: false,
		},
		{
			title: 'Message',
			width: '25%',
			align: ALIGNMENT.CENTER,
			key: 'message',
			sortable: false,
		},
	];
});

const notFoundText = computed(() => {
	if (search.value) {
		return {
			title: 'We couldn’t find any followers by that name.',
			description: 'Try checking the spelling or searching with a different name.',
		};
	}
	return {
		title: 'No attendees found',
		description: 'Your followers will be listed here once they join your group.',
	};
});

const nameSortDirection = computed(() => {
	if (sortMethod.value === FOLLOWERS_SORTING_METHOD.ALPHABETICAL_A2Z) {
		return ARROW_ICON_DIRECTION.DOWN;
	} else if (sortMethod.value === FOLLOWERS_SORTING_METHOD.ALPHABETICAL_Z2A) {
		return ARROW_ICON_DIRECTION.UP;
	}
	return '';
});

const roleSortDirection = computed(() => {
	if (sortMethod.value === FOLLOWERS_SORTING_METHOD.ROLE_ASC) {
		return ARROW_ICON_DIRECTION.DOWN;
	} else if (sortMethod.value === FOLLOWERS_SORTING_METHOD.ROLE_DESC) {
		return ARROW_ICON_DIRECTION.UP;
	}
	return '';
});

async function fetchFollowers() {
	if (!props.groupId) {
		return;
	}

	await profileStore.getFollowerList(props.groupId, {
		itemPerPage: PAGINATION_CONFIG.ITEM_PER_PAGE,
		currentPage: currentPage.value,
		sortBy: sortMethod.value,
		...(search.value ? { filter: `name:${search.value}` } : {}),
	});

	followerData.value = profileStore.follower?.data?.data || [];
	paginationInfo.value = profileStore.follower?.data?.paginationInfo || null;
}

function handlePageChange(page: number) {
	currentPage.value = page;
	fetchFollowers();
}

function handleNameSort() {
	// Toggle sort direction
	sortMethod.value =
		sortMethod.value === FOLLOWERS_SORTING_METHOD.ALPHABETICAL_A2Z
			? FOLLOWERS_SORTING_METHOD.ALPHABETICAL_Z2A
			: FOLLOWERS_SORTING_METHOD.ALPHABETICAL_A2Z;

	// Reset to first page when changing sort
	currentPage.value = 1;
	fetchFollowers();
}

function handleRoleSort() {
	if (sortMethod.value === FOLLOWERS_SORTING_METHOD.ROLE_ASC) {
		sortMethod.value = FOLLOWERS_SORTING_METHOD.ROLE_DESC;
	} else {
		sortMethod.value = FOLLOWERS_SORTING_METHOD.ROLE_ASC;
	}

	// Reset to first page when changing sort
	currentPage.value = 1;
	fetchFollowers();
}

function handleConfirmPromote(userId: number, userName: string) {
	processingName.value = userName;
	processingId.value = userId;
	isConfirmPromote.value = true;
}

async function handlePromote() {
	if (!props.groupId || !processingId.value) {
		return;
	}

	try {
		isProcessing.value = true;

		await groupStore.updateGroupFollower(props.groupId, {
			userIds: [processingId.value],
			role: GROUP_ROLE.ADMIN,
		});
		await fetchFollowers();
	} catch {
		notificationStore.showErrorNotification(COMMON_ERROR_MESSAGE.TRY_AGAIN);
	} finally {
		isProcessing.value = false;
		processingId.value = null;
		processingName.value = null;
		isConfirmPromote.value = false;
	}
}

function handleConfirmDemote(userId: number) {
	processingId.value = userId;
	isConfirmDemote.value = true;
}

async function handleDemote() {
	if (!props.groupId || !processingId.value) return;

	try {
		isProcessing.value = true;
		await groupStore.updateGroupFollower(props.groupId, {
			userIds: [processingId.value],
			role: GROUP_ROLE.FOLLOWER,
		});
		await fetchFollowers();
	} catch {
		notificationStore.showErrorNotification(COMMON_ERROR_MESSAGE.TRY_AGAIN);
	} finally {
		isProcessing.value = false;
		processingId.value = null;
		processingName.value = null;
		isConfirmDemote.value = false;
	}
}

function handleMessage(userId: number, userName: string) {
	emit('onMessage', userId, userName);
}

defineExpose({
	fetchFollowers,
});

onMounted(() => {
	// Reset the current page
	currentPage.value = 1;
	// Initial fetch
	fetchFollowers();
});

// Watch for search term changes
watch(
	() => search.value,
	() => {
		currentPage.value = 1; // Reset to first page when searching
		fetchFollowers();
	}
);

// Watch for group ID changes
watch(
	() => props.groupId,
	() => {
		currentPage.value = 1; // Reset to first page when group changes
		fetchFollowers();
	}
);
</script>

<template>
	<div class="cohost-content">
		<div class="search-container">
			<template v-if="isLoading && !followerData?.length">
				<ButtonSkeletonLoader class="search-input" height="44" />
			</template>
			<template v-else>
				<BaseTextInput
					v-model="search"
					class="search-input"
					placeholder="Search follower"
					:disabled="isLoading"
				>
					<template #leftIcon>
						<SearchIcon :color="ICON_COLOR.DEFAULT" />
					</template>
				</BaseTextInput>
			</template>
		</div>

		<VDataTable
			density="compact"
			:show-current-page="false"
			:headers="(headers as any) || []"
			:items="followerData || []"
		>
			<!-- eslint-disable-next-line vue/valid-v-slot -->
			<template #header.name="{ column }">
				<div v-if="isTablet" class="sort-column-wrapper">
					<div class="sort-header" @click="handleNameSort">
						<p>{{ column.title }}</p>
						<ArrowSortingIcon :direction="nameSortDirection" />
					</div>
					<p>/</p>
					<div class="sort-header" @click="handleRoleSort">
						<p>Role</p>
						<ArrowSortingIcon :direction="roleSortDirection" />
					</div>
				</div>
				<div v-else class="sort-column-wrapper" @click="handleNameSort">
					<p>{{ column.title }}</p>
					<ArrowSortingIcon :direction="nameSortDirection" />
				</div>
			</template>
			<!-- eslint-disable-next-line vue/valid-v-slot -->
			<template #header.role="{ column }">
				<div class="sort-column-wrapper" @click="handleRoleSort">
					<p>{{ column.title }}</p>
					<ArrowSortingIcon :direction="roleSortDirection" />
				</div>
			</template>
			<template #no-data>
				<AttendeeListSkeletonLoader v-if="isLoading" />
				<div v-else class="table-no-data">
					<SearchIcon class="icon" width="24" height="24" />
					<p class="header text-md text-semibold">{{ notFoundText.title }}</p>
					<p class="text-sm text-regular">
						{{ notFoundText.description }}
					</p>
				</div>
			</template>
			<template #item="{ item }">
				<ManageFollowerItem
					:follower="item"
					:is-loading="isLoading"
					:is-admin-view="isAdmin"
					:processing-id="processingId"
					@on-message="handleMessage"
					@on-promote="handleConfirmPromote"
					@on-demote="handleConfirmDemote"
				/>
			</template>
			<template #bottom>
				<BasePagination
					:page="currentPage"
					:total-pages="paginationInfo?.totalPages"
					:is-loading="isLoading"
					@on-page-change="handlePageChange"
				/>
			</template>
		</VDataTable>

		<!-- Add as Co-organizer dialog -->
		<ConfirmDialog
			:is-show="isConfirmPromote"
			:is-loading="isLoading"
			confirm-button-name="Confirm"
			cancel-button-name="Go back"
			@on-close="isConfirmPromote = false"
			@on-confirm="handlePromote"
		>
			<template #header>
				<UsersPlusIcon class="icon-header-confirm-dlg icon-info" :color="ICON_COLOR.PRIMARY" />
			</template>
			<template #title> Add as a Co-organizer </template>
			<template #content>
				<p class="text-sm text-regular">
					<span
						>Co-organizers can create and edit event details, manage ticket sales and view attendee
						information. If you would like to add
					</span>
					<strong>{{ processingName || 'follower' }} </strong>
					<span> as a Co-organizer, please confirm.</span>
				</p>
			</template>
		</ConfirmDialog>

		<!-- Remove Co-organizer dialog -->
		<ConfirmDialog
			:is-show="isConfirmDemote"
			:is-loading="isLoading"
			confirm-button-name="Confirm"
			cancel-button-name="Go back"
			@on-close="isConfirmDemote = false"
			@on-confirm="handleDemote"
		>
			<template #header>
				<UsersPlusIcon class="icon-header-confirm-dlg icon-error" :color="ICON_COLOR.ERROR" />
			</template>
			<template #title> Remove as a Co-organizer </template>
			<template #content>
				<p class="text-sm text-regular">
					Removing a Co-organizer will revoke their access to editing event details, managing ticket
					sales and viewing attendee information.
				</p>
			</template>
		</ConfirmDialog>
	</div>
</template>

<style scoped lang="scss">
.cohost-content {
	display: flex;
	flex-direction: column;
	gap: spacings-get(4);
	padding-top: spacings-get(4);

	.search-container {
		display: flex;
		padding-left: spacings-get(1);

		.search-input {
			width: rem(434);

			@include media-query-max(tablet) {
				width: 100%;
			}
		}

		svg {
			margin-left: rem(10);
		}
	}

	.pagination-container {
		margin-top: spacings-get(5);

		button {
			padding: 0;
		}

		.mobile-pagination {
			display: flex;
			justify-content: space-between;
			align-items: center;

			button {
				width: rem(90);
				display: flex;
				justify-content: flex-end;
			}

			.pagination-info span {
				@include fonts-weight(medium);
			}
		}
	}
}
</style>
